import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import type {
  Batch,
  Currency,
  Dispatch,
  DispatchLine,
  Location,
  Movement,
  MovementType,
  Product,
  Receipt,
  ReceiptLine,
  ReorderRule,
  Transfer,
  TransferLine,
} from '@/data/inventory/types'
import { BASE_CURRENCY } from '@/data/inventory/format'
import { setLocations } from '@/data/inventory/locations'
import { productById, setProducts } from '@/data/inventory/products'
import { apiUpload } from '@/app/core/http/apiClient'

/* ───────────────────────────  GraphQL  ─────────────────────────── */

const GET_INV_PRODUCTS = gql`
  query InvProducts {
    products {
      id
      sku
      name
      description
      category
      hsnCode
      baseUomCode
      baseUomName
      active
      gstRate
      productType
      imageUrl
      reorderRules {
        warehouseId
        isGlobal
        reorderPoint
        reorderQuantity
        safetyStock
      }
    }
  }
`

const GET_INV_WAREHOUSES = gql`
  query InvWarehouses {
    warehouses {
      id
      code
      name
      city
      state
      warehouseType
      active
    }
  }
`

const GET_INV_STOCK_LOTS = gql`
  query InvStockLots {
    stockLots {
      id
      productId
      warehouseId
      lotNumber
      expiryDate
      manufactureDate
      receivedDate
      quantityOnHand
      quantityReserved
      quantityAvailable
      quantityDamaged
      costPrice
    }
  }
`

const GET_INV_MOVEMENTS = gql`
  query InvMovements {
    movements {
      id
      movementNumber
      movementType
      status
      sourceWarehouseId
      destinationWarehouseId
      movementDate
      reference
      reason
      createdAt
      confirmedAt
      confirmedBy
      lines {
        id
        productId
        stockLotId
        lotNumber
        quantity
        expiryDate
        costPrice
      }
    }
  }
`

const CREATE_MOVEMENT = gql`
  mutation InvCreateMovement($input: MovementInput!) {
    createMovement(input: $input) {
      id
      movementNumber
      status
    }
  }
`

const CONFIRM_MOVEMENT = gql`
  mutation InvConfirmMovement($id: ID!) {
    confirmMovement(id: $id) {
      id
      status
    }
  }
`

const MARK_STOCK_DAMAGED = gql`
  mutation InvMarkStockDamaged(
    $productId: ID!
    $warehouseId: ID!
    $lotId: ID
    $quantity: String!
    $note: String
  ) {
    markStockDamaged(
      productId: $productId
      warehouseId: $warehouseId
      lotId: $lotId
      quantity: $quantity
      note: $note
    ) {
      id
      quantityDamaged
    }
  }
`

/* ───────────────────────────  Raw GQL shapes  ─────────────────────────── */

interface GqlProduct {
  id: string
  sku: string
  name: string
  description?: string | null
  category?: string | null
  hsnCode?: string | null
  baseUomCode: string
  baseUomName: string
  active: boolean
  gstRate?: string | null
  productType?: string | null
  imageUrl?: string | null
  reorderRules?: {
    warehouseId?: string | null
    isGlobal: boolean
    reorderPoint: string
    reorderQuantity: string
    safetyStock?: string
  }[]
}

interface GqlWarehouse {
  id: string
  code: string
  name: string
  city?: string | null
  state?: string | null
  warehouseType: 'OWN' | 'THIRD_PARTY'
  active: boolean
}

interface GqlLot {
  id: string
  productId: string
  warehouseId: string
  lotNumber: string
  expiryDate?: string | null
  manufactureDate?: string | null
  receivedDate?: string | null
  quantityOnHand: string
  quantityReserved?: string | null
  quantityAvailable?: string | null
  quantityDamaged?: string | null
  costPrice?: string | null
}

interface GqlMovementLine {
  id?: string | null
  productId: string
  stockLotId?: string | null
  lotNumber?: string | null
  quantity: string
  expiryDate?: string | null
  costPrice?: string | null
}

interface GqlMovement {
  id: string
  movementNumber: string
  movementType: 'RECEIPT' | 'ISSUE' | 'TRANSFER' | 'ADJUSTMENT' | 'RETURN'
  status: 'DRAFT' | 'CONFIRMED' | 'CANCELLED'
  sourceWarehouseId?: string | null
  destinationWarehouseId?: string | null
  movementDate: string
  reference?: string | null
  reason?: string | null
  createdAt: string
  confirmedAt?: string | null
  confirmedBy?: string | null
  lines: GqlMovementLine[]
}

/* ───────────────────────────  Mappers  ─────────────────────────── */

function mapWarehouse(w: GqlWarehouse): Location {
  return {
    id: w.id,
    code: w.code,
    name: w.name,
    type: w.warehouseType === 'THIRD_PARTY' ? '3pl' : 'warehouse',
    region: 'IN',
    currency: 'INR',
    complianceProfile: 'fssai',
    city: [w.city, w.state].filter(Boolean).join(', '),
    active: w.active,
  }
}

function mapLot(lot: GqlLot): Batch {
  return {
    id: lot.id,
    productId: lot.productId,
    batchNumber: lot.lotNumber,
    quantity: Number(lot.quantityOnHand) || 0,
    expiryDate: lot.expiryDate ?? '2099-12-31',
    manufactureDate: lot.manufactureDate ?? undefined,
    locationId: lot.warehouseId,
    sourceReceiptId: null,
    hold: null,
    damaged: Number(lot.quantityDamaged) || 0,
    reserved: Number(lot.quantityReserved) || 0,
    receivedAt: lot.receivedDate ?? new Date().toISOString(),
  }
}

function mapProduct(p: GqlProduct, costByProduct: Map<string, number>): Product {
  const reorder =
    (p.reorderRules ?? []).find((r) => r.isGlobal) ?? (p.reorderRules ?? [])[0]
  return {
    id: p.id,
    sku: p.sku,
    name: p.name,
    category: p.category || 'Uncategorised',
    baseUnit: p.baseUomCode,
    units: [{ code: p.baseUomCode, label: p.baseUomName, perBase: 1 }],
    reorderPoint: reorder ? Number(reorder.reorderPoint) || 0 : 0,
    avgDailyUsage: 0,
    supplierRef: '',
    cost: { amount: costByProduct.get(p.id) ?? 0, currency: 'INR' },
    custom: {
      hsnCode: p.hsnCode ?? undefined,
      ...(p.gstRate ? { gstRate: Number(p.gstRate) } : {}),
    },
    description: p.description ?? undefined,
    productType: (p.productType as Product['productType']) ?? 'FINISHED_GOOD',
    imageUrl: p.imageUrl ?? undefined,
  }
}

function mapMovementType(
  type: GqlMovement['movementType'],
  qty: number,
  reason?: string | null,
): MovementType {
  switch (type) {
    case 'RECEIPT':
      return 'receive'
    case 'ISSUE':
      return 'dispatch'
    case 'TRANSFER':
      return 'transfer'
    case 'RETURN':
      return 'return'
    case 'ADJUSTMENT':
      return qty < 0 && (reason ?? '').toLowerCase().includes('write')
        ? 'write_off'
        : 'adjust'
    default:
      return 'adjust'
  }
}

function signedDelta(type: GqlMovement['movementType'], qty: number): number {
  switch (type) {
    case 'RECEIPT':
    case 'RETURN':
    case 'TRANSFER':
      return Math.abs(qty)
    case 'ISSUE':
      return -Math.abs(qty)
    case 'ADJUSTMENT':
      return qty
    default:
      return qty
  }
}

/* ───────────────────────────  Context  ─────────────────────────── */

interface InventoryContextValue {
  products: Product[]
  locations: Location[]
  batches: Batch[]
  movements: Movement[]
  receipts: Receipt[]
  transfers: Transfer[]
  dispatches: Dispatch[]
  reorderRules: ReorderRule[]
  reportingCurrency: Currency
  setReportingCurrency: (c: Currency) => void
  confirmReceipt: (input: {
    warehouseId: string
    poRef: string | null
    receiptDate: string
    lines: ReceiptLine[]
  }) => Receipt
  confirmTransfer: (input: {
    fromLocationId: string
    toLocationId: string
    date: string
    lines: TransferLine[]
  }) => Transfer
  /**
   * Confirms an outbound dispatch (ISSUE movement). The backend auto-selects batches (FEFO)
   * and deducts stock. Async + rejects on failure (e.g. insufficient stock) so the caller can
   * surface the error to the warehouse user.
   */
  confirmDispatch: (input: {
    warehouseId: string
    reference: string | null
    dispatchDate: string
    /** Outbound QC sign-off — the backend hard-gates ISSUE confirmation on this being true. */
    qcPassed: boolean
    lines: DispatchLine[]
  }) => Promise<void>
  adjustStock: (input: {
    productId: string
    locationId: string
    batchId?: string | null
    newQuantity: number
    reason: string
  }) => void
  writeOffBatch: (batchId: string, quantity: number, reason: string) => void
  setBatchHold: (
    batchId: string,
    hold: 'quarantined' | 'damaged' | null,
    reason?: string,
  ) => void
  /**
   * Moves stock into the damaged bucket. The backend reduces on-hand on the chosen (or FEFO) lot,
   * bumps its damaged tally, and records a "Damaged" adjustment in the ledger. Async + rejects on
   * failure (e.g. damaging more than available) so the caller can surface the error.
   */
  markDamaged: (input: {
    productId: string
    warehouseId: string
    batchId?: string | null
    quantity: number
    note?: string
  }) => Promise<void>
  /** Uploads (or replaces) a product's primary image via the REST endpoint, then refetches the catalog. */
  uploadProductImage: (productId: string, file: File) => Promise<void>
}

const InventoryContext = createContext<InventoryContextValue | null>(null)

let seq = 1000
const uid = (p: string) => `${p}-${++seq}`

export function InventoryProvider({ children }: { children: ReactNode }) {
  const productsQ = useQuery(GET_INV_PRODUCTS)
  const warehousesQ = useQuery(GET_INV_WAREHOUSES)
  const lotsQ = useQuery(GET_INV_STOCK_LOTS)
  const movementsQ = useQuery(GET_INV_MOVEMENTS)

  const [createMovement] = useMutation(CREATE_MOVEMENT)
  const [confirmMovement] = useMutation(CONFIRM_MOVEMENT)
  const [markStockDamagedMut] = useMutation(MARK_STOCK_DAMAGED)

  const [reportingCurrency, setReportingCurrencyState] = useState<Currency>(() => {
    if (typeof window === 'undefined') return BASE_CURRENCY
    return window.localStorage.getItem('xlops.reportingCurrency') || BASE_CURRENCY
  })
  const setReportingCurrency = useCallback((c: Currency) => {
    setReportingCurrencyState(c)
    if (typeof window !== 'undefined')
      window.localStorage.setItem('xlops.reportingCurrency', c)
  }, [])

  const rawProducts: GqlProduct[] = productsQ.data?.products ?? []
  const rawWarehouses: GqlWarehouse[] = warehousesQ.data?.warehouses ?? []
  const rawLots: GqlLot[] = lotsQ.data?.stockLots ?? []
  const rawMovements: GqlMovement[] = movementsQ.data?.movements ?? []

  const batches = useMemo(() => rawLots.map(mapLot), [rawLots])

  const locations = useMemo(() => rawWarehouses.map(mapWarehouse), [rawWarehouses])

  const products = useMemo(() => {
    // weighted-average cost per product from its lots
    const totals = new Map<string, { qty: number; value: number }>()
    for (const lot of rawLots) {
      const qty = Number(lot.quantityOnHand) || 0
      const cost = Number(lot.costPrice) || 0
      const cur = totals.get(lot.productId) ?? { qty: 0, value: 0 }
      cur.qty += qty
      cur.value += qty * cost
      totals.set(lot.productId, cur)
    }
    const costByProduct = new Map<string, number>()
    for (const [pid, t] of totals)
      costByProduct.set(pid, t.qty > 0 ? t.value / t.qty : 0)
    return rawProducts.map((p) => mapProduct(p, costByProduct))
  }, [rawProducts, rawLots])

  const movements = useMemo<Movement[]>(() => {
    const out: Movement[] = []
    for (const m of rawMovements) {
      (m.lines ?? []).forEach((l, idx) => {
        const qty = Number(l.quantity) || 0
        out.push({
          id: l.id || `${m.id}-${idx}`,
          code: m.movementNumber,
          type: mapMovementType(m.movementType, qty, m.reason),
          productId: l.productId,
          batchId: l.stockLotId ?? null,
          quantityDelta: signedDelta(m.movementType, qty),
          fromLocationId: m.sourceWarehouseId ?? null,
          toLocationId: m.destinationWarehouseId ?? null,
          userId: m.confirmedBy ?? 'system',
          reason: m.reason ?? null,
          refId: m.reference ?? null,
          createdAt: m.createdAt || m.movementDate,
          confirmedAt: m.confirmedAt ?? undefined,
        })
      })
    }
    return out.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
  }, [rawMovements])

  const receipts = useMemo<Receipt[]>(
    () =>
      rawMovements
        .filter((m) => m.movementType === 'RECEIPT')
        .map((m) => ({
          id: m.id,
          code: m.movementNumber,
          warehouseId: m.destinationWarehouseId ?? '',
          poRef: m.reference ?? null,
          receiptDate: m.movementDate,
          lines: (m.lines ?? []).map((l) => ({
            productId: l.productId,
            quantity: Number(l.quantity) || 0,
            unit: productById(l.productId)?.baseUnit ?? 'pcs',
            batchNumber: l.lotNumber ?? '',
            expiryDate: l.expiryDate ?? null,
            unitCost: l.costPrice != null ? Number(l.costPrice) : null,
            locationId: m.destinationWarehouseId ?? '',
          })),
          status: m.status === 'DRAFT' ? 'draft' : 'confirmed',
          createdAt: m.createdAt || m.movementDate,
        })),
    [rawMovements],
  )

  const transfers = useMemo<Transfer[]>(
    () =>
      rawMovements
        .filter((m) => m.movementType === 'TRANSFER')
        .map((m) => ({
          id: m.id,
          code: m.movementNumber,
          fromLocationId: m.sourceWarehouseId ?? '',
          toLocationId: m.destinationWarehouseId ?? '',
          date: m.movementDate,
          lines: (m.lines ?? []).map((l) => ({
            productId: l.productId,
            quantity: Number(l.quantity) || 0,
            batchId: l.stockLotId ?? null,
          })),
          status: m.status === 'CONFIRMED' ? 'completed' : 'pending',
          createdAt: m.createdAt || m.movementDate,
        })),
    [rawMovements],
  )

  const dispatches = useMemo<Dispatch[]>(
    () =>
      rawMovements
        .filter((m) => m.movementType === 'ISSUE')
        .map((m) => ({
          id: m.id,
          code: m.movementNumber,
          warehouseId: m.sourceWarehouseId ?? '',
          reference: m.reference ?? null,
          dispatchDate: m.movementDate,
          lines: (m.lines ?? []).map((l) => ({
            productId: l.productId,
            quantity: Number(l.quantity) || 0,
          })),
          status: m.status === 'DRAFT' ? 'draft' : 'confirmed',
          createdAt: m.createdAt || m.movementDate,
        })),
    [rawMovements],
  )

  const reorderRules = useMemo<ReorderRule[]>(() => {
    const out: ReorderRule[] = []
    for (const p of rawProducts) {
      ;(p.reorderRules ?? []).forEach((r, idx) => {
        out.push({
          id: `${p.id}-rr-${idx}`,
          scope: r.isGlobal ? 'global' : 'sku',
          productId: p.id,
          warehouseId: r.warehouseId ?? undefined,
          threshold: Number(r.reorderPoint) || 0,
          suggestedQty: Number(r.reorderQuantity) || 0,
          safetyStock: Number(r.safetyStock) || 0,
        })
      })
    }
    // Global rules first, then per-SKU
    return out.sort((a, b) => (a.scope === b.scope ? 0 : a.scope === 'global' ? -1 : 1))
  }, [rawProducts])

  // Populate the synchronous registries used by productById / locationById.
  setProducts(products)
  setLocations(locations)

  const refetchAll = useCallback(() => {
    void lotsQ.refetch()
    void movementsQ.refetch()
    void productsQ.refetch()
  }, [lotsQ, movementsQ, productsQ])

  const runMovement = useCallback(
    async (input: Record<string, unknown>) => {
      try {
        const res = await createMovement({ variables: { input } })
        const id = res.data?.createMovement?.id
        if (id) await confirmMovement({ variables: { id } })
      } finally {
        refetchAll()
      }
    },
    [createMovement, confirmMovement, refetchAll],
  )

  const confirmReceipt = useCallback<InventoryContextValue['confirmReceipt']>(
    (input) => {
      void runMovement({
        movementType: 'RECEIPT',
        destinationWarehouseId: input.warehouseId,
        movementDate: input.receiptDate,
        reference: input.poRef ?? undefined,
        lines: input.lines.map((l) => ({
          productId: l.productId,
          quantity: String(l.quantity),
          lotNumber: l.batchNumber || undefined,
          expiryDate: l.expiryDate || undefined,
          costPrice:
            l.unitCost != null && !Number.isNaN(l.unitCost)
              ? String(l.unitCost)
              : undefined,
        })),
      })
      return {
        id: uid('rcv'),
        code: 'RCV-PENDING',
        warehouseId: input.warehouseId,
        poRef: input.poRef,
        receiptDate: input.receiptDate,
        lines: input.lines,
        status: 'confirmed',
        createdAt: new Date().toISOString(),
      }
    },
    [runMovement],
  )

  const confirmTransfer = useCallback<InventoryContextValue['confirmTransfer']>(
    (input) => {
      void runMovement({
        movementType: 'TRANSFER',
        sourceWarehouseId: input.fromLocationId,
        destinationWarehouseId: input.toLocationId,
        movementDate: input.date,
        lines: input.lines.map((l) => ({
          productId: l.productId,
          quantity: String(l.quantity),
          stockLotId: l.batchId ?? undefined,
        })),
      })
      return {
        id: uid('trf'),
        code: 'TRF-PENDING',
        fromLocationId: input.fromLocationId,
        toLocationId: input.toLocationId,
        date: input.date,
        lines: input.lines,
        status: 'completed',
        createdAt: new Date().toISOString(),
      }
    },
    [runMovement],
  )

  const confirmDispatch = useCallback<InventoryContextValue['confirmDispatch']>(
    async (input) => {
      // ISSUE with no stockLotId per line → the backend selects batches automatically
      // (FEFO per inventory.lot-selection-strategy) and deducts on confirm. Awaited so an
      // insufficient-stock error propagates to the page instead of being silently swallowed.
      await runMovement({
        movementType: 'ISSUE',
        sourceWarehouseId: input.warehouseId,
        movementDate: input.dispatchDate,
        reference: input.reference ?? undefined,
        qcPassed: input.qcPassed,
        lines: input.lines.map((l) => ({
          productId: l.productId,
          quantity: String(l.quantity),
        })),
      })
    },
    [runMovement],
  )

  const adjustStock = useCallback<InventoryContextValue['adjustStock']>(
    (input) => {
      const current =
        batches.find((b) =>
          input.batchId
            ? b.id === input.batchId
            : b.productId === input.productId && b.locationId === input.locationId,
        )?.quantity ?? 0
      const delta = input.newQuantity - current
      if (delta === 0) return
      // ADJUSTMENT is keyed off destinationWarehouseId by the backend. A negative
      // delta reduces the warehouse's FIFO lot; a positive delta adds stock.
      void runMovement({
        movementType: 'ADJUSTMENT',
        destinationWarehouseId: input.locationId,
        reason: input.reason,
        lines: [
          {
            productId: input.productId,
            stockLotId: input.batchId ?? undefined,
            quantity: String(delta),
          },
        ],
      })
    },
    [batches, runMovement],
  )

  const writeOffBatch = useCallback<InventoryContextValue['writeOffBatch']>(
    (batchId, quantity, reason) => {
      const batch = batches.find((b) => b.id === batchId)
      if (!batch) return
      void runMovement({
        movementType: 'ADJUSTMENT',
        destinationWarehouseId: batch.locationId,
        reason: `Write-off: ${reason}`,
        lines: [
          {
            productId: batch.productId,
            stockLotId: batchId,
            quantity: String(-Math.abs(quantity)),
          },
        ],
      })
    },
    [batches, runMovement],
  )

  // Quarantine / release omitted (decision D3 — no hold field on stock lots).
  const setBatchHold = useCallback<InventoryContextValue['setBatchHold']>(() => {
    /* no-op */
  }, [])

  const markDamaged = useCallback<InventoryContextValue['markDamaged']>(
    async (input) => {
      try {
        // errorPolicy 'all' means a failure comes back in `errors` rather than throwing — surface it.
        const res = await markStockDamagedMut({
          variables: {
            productId: input.productId,
            warehouseId: input.warehouseId,
            lotId: input.batchId ?? null,
            quantity: String(input.quantity),
            note: input.note ?? null,
          },
        })
        if (res.errors?.length) throw new Error(res.errors[0].message)
      } finally {
        refetchAll()
      }
    },
    [markStockDamagedMut, refetchAll],
  )

  const uploadProductImage = useCallback<InventoryContextValue['uploadProductImage']>(
    async (productId, file) => {
      // Multipart upload can't go through Apollo — route it through the shared authed REST client.
      const form = new FormData()
      form.append('file', file)
      await apiUpload(`/products/${productId}/image`, form)
      refetchAll()
    },
    [refetchAll],
  )

  const value = useMemo<InventoryContextValue>(
    () => ({
      products,
      locations,
      batches,
      movements,
      receipts,
      transfers,
      dispatches,
      reorderRules,
      reportingCurrency,
      setReportingCurrency,
      confirmReceipt,
      confirmTransfer,
      confirmDispatch,
      adjustStock,
      writeOffBatch,
      setBatchHold,
      markDamaged,
      uploadProductImage,
    }),
    [
      products,
      locations,
      batches,
      movements,
      receipts,
      transfers,
      dispatches,
      reorderRules,
      reportingCurrency,
      setReportingCurrency,
      confirmReceipt,
      confirmTransfer,
      confirmDispatch,
      adjustStock,
      writeOffBatch,
      setBatchHold,
      markDamaged,
      uploadProductImage,
    ],
  )

  const initialLoading =
    (productsQ.loading && rawProducts.length === 0) ||
    (warehousesQ.loading && rawWarehouses.length === 0)

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-sm text-muted-foreground">Loading inventory…</div>
      </div>
    )
  }

  return (
    <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>
  )
}

export function useInventory(): InventoryContextValue {
  const ctx = useContext(InventoryContext)
  if (!ctx) throw new Error('useInventory must be used within InventoryProvider')
  return ctx
}

export type { MovementType }
